import { useCallback, useEffect, useMemo, useState } from 'react';

export interface RangeFilterPatch {
  min: number | undefined;
  max: number | undefined;
}

export interface UseRangeFilterOptions {
  min: number | undefined;
  max: number | undefined;
  defaultMax: number;
  absoluteMax: number;
  onPatch: (patch: RangeFilterPatch) => void;
  onSearchWithPatch: (patch: RangeFilterPatch) => void;
  /** Por defecto usa onSearchWithPatch (inventario). Catálogo puede pasar onPatch. */
  onSliderCommit?: (patch: RangeFilterPatch) => void;
}

function clamp(value: number, absoluteMax: number): number {
  return Math.max(0, Math.min(value, absoluteMax));
}

/** Actualiza solo el mínimo; el máximo solo cambia si min > max con valores válidos. */
export function patchMinBound(
  value: number | undefined,
  currentMin: number | undefined,
  currentMax: number | undefined,
  defaultMax: number,
  absoluteMax: number,
): RangeFilterPatch & { slider: [number, number] } {
  const effectiveMax = currentMax ?? defaultMax;
  if (value === undefined) {
    return {
      min: undefined,
      max: currentMax,
      slider: [0, effectiveMax],
    };
  }
  const newMin = clamp(value, absoluteMax);
  let max = currentMax;
  let sliderMax = effectiveMax;
  if (newMin > 0 && newMin > effectiveMax) {
    max = newMin;
    sliderMax = newMin;
  }
  return {
    min: newMin === 0 ? undefined : newMin,
    max,
    slider: [newMin, sliderMax],
  };
}

/** Actualiza solo el máximo; el mínimo solo baja si max < min con valores válidos. */
export function patchMaxBound(
  value: number | undefined,
  currentMin: number | undefined,
  currentMax: number | undefined,
  defaultMax: number,
  absoluteMax: number,
): RangeFilterPatch & { slider: [number, number] } {
  if (value === undefined) {
    return {
      min: currentMin,
      max: undefined,
      slider: [currentMin ?? 0, defaultMax],
    };
  }
  const newMax = clamp(value, absoluteMax);
  let min = currentMin;
  if (newMax > 0 && currentMin != null && currentMin > newMax) {
    min = newMax;
  }
  const sliderMax = newMax === 0 ? defaultMax : newMax;
  return {
    min: min === 0 ? undefined : min,
    max: newMax === 0 ? undefined : newMax,
    slider: [min ?? 0, sliderMax],
  };
}

export function useRangeFilter({
  min,
  max,
  defaultMax,
  absoluteMax,
  onPatch,
  onSearchWithPatch,
  onSliderCommit,
}: UseRangeFilterOptions) {
  const [sliderTemp, setSliderTemp] = useState<[number, number]>([min ?? 0, max ?? defaultMax]);

  useEffect(() => {
    setSliderTemp([min ?? 0, max ?? defaultMax]);
  }, [min, max, defaultMax]);

  const sliderAxisMax = useMemo(
    () => Math.min(absoluteMax, Math.max(defaultMax, sliderTemp[1], max ?? 0)),
    [absoluteMax, defaultMax, sliderTemp, max],
  );

  const applyMin = useCallback(
    (value: number | undefined, search: boolean) => {
      const result = patchMinBound(value, min, max, defaultMax, absoluteMax);
      setSliderTemp(result.slider);
      const patch = { min: result.min, max: result.max };
      if (search) onSearchWithPatch(patch);
      else onPatch(patch);
    },
    [min, max, defaultMax, absoluteMax, onPatch, onSearchWithPatch],
  );

  const applyMax = useCallback(
    (value: number | undefined, search: boolean) => {
      const result = patchMaxBound(value, min, max, defaultMax, absoluteMax);
      setSliderTemp(result.slider);
      const patch = { min: result.min, max: result.max };
      if (search) onSearchWithPatch(patch);
      else onPatch(patch);
    },
    [min, max, defaultMax, absoluteMax, onPatch, onSearchWithPatch],
  );

  const onSliderChange = useCallback((_: unknown, value: number | number[]) => {
    setSliderTemp(value as [number, number]);
  }, []);

  const commitSlider = onSliderCommit ?? onSearchWithPatch;

  const onSliderCommitted = useCallback(
    (_: unknown, value: number | number[]) => {
      const [sMin, sMax] = value as number[];
      setSliderTemp([sMin, sMax]);
      commitSlider({
        min: sMin === 0 ? undefined : sMin,
        max: sMax >= sliderAxisMax ? undefined : sMax,
      });
    },
    [commitSlider, sliderAxisMax],
  );

  return {
    sliderTemp,
    sliderAxisMax,
    applyMin,
    applyMax,
    onSliderChange,
    onSliderCommitted,
  };
}
